import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Check, ChevronDown, Gamepad2, Scissors } from "lucide-react";
import { createUserWithEmailAndPassword, deleteUser, signOut } from "firebase/auth";
import { collection, getDocs, setDoc, doc } from "firebase/firestore";
import { auth, db } from '../Context/firebaseConfig';
import Edit from "../images/ETAsignup.webp";
import UnusableWords from "../Components/unusableWords";
import axios from "axios";
import SEO from "../Components/SEO";
import { trackStatsEvent } from "../utils/statsAnalytics";
import { trackEvent } from "../utils/analytics";
import { useAuth } from "../Context/AuthContext";
import { getAuthErrorMessage, logAuthError } from "../utils/firebaseAuthErrors";
import { createPricingCheckoutSession } from "../utils/pricingCheckout";
import {
    getSignupIntent,
    roleLabelForSignup,
} from "../utils/subscriptionAccount";
import { getSafeNextPath, withNextPath } from "../utils/navigation";
import { assignSafeExternalUrl } from "../utils/security";
import { listPublicProfiles } from "../services/publicProfiles";

const CREATOR_FOCUS_OPTIONS = [
    {
        value: "custom-editing",
        label: "Custom editing",
        description: "I want Twitch VODs, highlights, or YouTube videos edited.",
    },
    {
        value: "clipping-campaigns",
        label: "Clipping campaigns",
        description: "I want stream moments turned into TikToks, Shorts, and Reels.",
    },
    {
        value: "editing-and-clipping",
        label: "Editing and clipping",
        description: "I want both deeper edits and short form growth content.",
    },
];

const EDITOR_FOCUS_OPTIONS = [
    {
        value: "clipping-campaigns",
        label: "Clipping campaigns",
        description: "I mainly want to clip streams for TikToks, Shorts, and Reels.",
    },
    {
        value: "short-form-edits",
        label: "Short form edits",
        description: "I make polished clips, captions, hooks, and vertical edits.",
    },
    {
        value: "twitch-vod-edits",
        label: "Twitch VOD edits",
        description: "I turn longer streams into highlights or YouTube videos.",
    },
    {
        value: "youtube-gaming-videos",
        label: "YouTube gaming videos",
        description: "I edit gaming videos, story based uploads, or full episodes.",
    },
    {
        value: "thumbnails-other",
        label: "Thumbnails or other",
        description: "I help creators with thumbnails, packaging, or related work.",
    },
];

const Signup = () => {

    const [formData, setFormData] = useState({
        email: "",
        username: "",
        password: "",
        role: "",
        contentFocus: "",
        id: ""
    });

    const [isPasswordValid, setPasswordValid] = useState(true);
    const [isUsernameInappropriate, setUsernameInappropriate] = useState(false);
    const [isUsernameTaken, setUsernameTaken] = useState(false);
    const [isUsernameInvalid, setUsernameInvalid] = useState(false);
    const [isUsernameChecking, setUsernameChecking] = useState(false);
    const [usernameCheckError, setUsernameCheckError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState(null);
    const [isEmailValid, setEmailValid] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [joinEmailList, setJoinEmailList] = useState(false);
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [gameOpen, setGameOpen] = useState(false);
    const isCreatingAccountRef = useRef(false);
    const usernameValidationRequestRef = useRef(0);
    const signupViewTrackedRef = useRef(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentUser, authLoading } = useAuth();
    const signupIntent = getSignupIntent(searchParams);
    const nextPath = getSafeNextPath(searchParams.get("next"));

    useEffect(() => {
        if (signupViewTrackedRef.current) return;
        signupViewTrackedRef.current = true;
        trackEvent("signup_viewed", {
            destination: nextPath === "/stats" || nextPath.startsWith("/stats?") ? "stats" : "other",
        });
    }, [nextPath]);

    const contentFocusOptions =
        formData.role === "Creator" ? CREATOR_FOCUS_OPTIONS : formData.role === "Editor" ? EDITOR_FOCUS_OPTIONS : [];

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        setCategoriesError(null);

        try {
            const categoriesCollection = collection(db, 'Categories');
            const categoriesSnapshot = await getDocs(categoriesCollection);

            if (!categoriesSnapshot.empty) {
                const categoriesList = categoriesSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        name: doc.data().categoryName,
                    }))
                    .filter((category) => category.name)
                    .sort((a, b) => {
                        const priority = ["TikTok clips", "YouTube", "IRL"];
                        const aPriority = priority.indexOf(a.name);
                        const bPriority = priority.indexOf(b.name);
                        if (aPriority !== -1 || bPriority !== -1) {
                            return (aPriority === -1 ? 99 : aPriority) - (bPriority === -1 ? 99 : bPriority);
                        }
                        return a.name.localeCompare(b.name);
                    });

                if (categoriesList.length > 0) {
                    setCategories(categoriesList);
                    return;
                }
            }

            const emptyCategoriesError = new Error("Signup categories collection is empty.");
            setCategories([]);
            setCategoriesError("We could not load categories right now. Please try again.");
            logAuthError("Signup categories empty", emptyCategoriesError, {
                flow: "signup",
            });
        } catch (error) {
            setCategories([]);
            setCategoriesError("We could not load categories right now. Please try again.");
            logAuthError("Signup categories fetch failed", error, {
                flow: "signup",
            });
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        const intendedRole = roleLabelForSignup(signupIntent?.requiredRole);
        if (!intendedRole) return;

        setFormData((prev) =>
            prev.role ? prev : { ...prev, role: intendedRole }
        );
    }, [signupIntent?.requiredRole]);

    useEffect(() => {
        const roleParam = searchParams.get("role")?.toLowerCase();
        const focusParam = searchParams.get("focus");
        const roleFromParam =
            roleParam === "creator"
                ? "Creator"
                : roleParam === "editor" || roleParam === "clipper"
                    ? "Editor"
                    : "";
        const validCreatorFocus = CREATOR_FOCUS_OPTIONS.some((option) => option.value === focusParam);
        const validEditorFocus = EDITOR_FOCUS_OPTIONS.some((option) => option.value === focusParam);

        if (!roleFromParam && !focusParam) return;

        setFormData((prev) => {
            const nextRole = prev.role || roleFromParam;
            const focusIsValid =
                nextRole === "Creator" ? validCreatorFocus : nextRole === "Editor" ? validEditorFocus : false;

            return {
                ...prev,
                role: nextRole,
                contentFocus: prev.contentFocus || (focusIsValid ? focusParam : ""),
            };
        });
    }, [searchParams]);

    useEffect(() => {
        if (!authLoading && currentUser && !isCreatingAccountRef.current) {
            navigate(nextPath);
        }
    }, [authLoading, currentUser, navigate, nextPath]);

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        if (name === "agreeToTerms") {
            setAgreeToTerms(checked);
        } else if (name === "joinEmailList") {
            setJoinEmailList(checked);
        }
    };

    const handleRoleChange = (role) => {
        setFormData({ ...formData, role, contentFocus: "" });
        setFormError(null);
    };

    const handleContentFocusChange = (contentFocus) => {
        setFormData({ ...formData, contentFocus });
        setFormError(null);
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePassword = (password) => {
        return password.length >= 8;
    };

    const validateUsername = async (username) => {
        const requestId = usernameValidationRequestRef.current + 1;
        usernameValidationRequestRef.current = requestId;

        if (!username) {
            setUsernameInvalid(false);
            setUsernameInappropriate(false);
            setUsernameTaken(false);
            setUsernameChecking(false);
            setUsernameCheckError(null);
            return;
        }

        const validUsername = /^[a-zA-Z0-9_]{3,20}$/.test(username);
        setUsernameInvalid(!validUsername);
        setUsernameCheckError(null);

        if (!validUsername) {
            setUsernameTaken(false);
            setUsernameChecking(false);
            return;
        }

        const inappropriate = UnusableWords.some(word => username.toLowerCase().includes(word.toLowerCase()));
        setUsernameInappropriate(inappropriate);

        if (inappropriate) {
            setUsernameTaken(false);
            setUsernameChecking(false);
            return;
        }

        try {
            setUsernameChecking(true);
            const matchingProfiles = await listPublicProfiles({ username });
            if (requestId !== usernameValidationRequestRef.current) return;
            setUsernameTaken(matchingProfiles.length > 0);
        } catch (error) {
            if (requestId !== usernameValidationRequestRef.current) return;
            setUsernameTaken(false);
            setUsernameCheckError("We could not check that username. Please try again.");
            logAuthError("Signup username validation failed", error, {
                flow: "signup",
            });
        } finally {
            if (requestId === usernameValidationRequestRef.current) {
                setUsernameChecking(false);
            }
        }
    };

    const handleChange = async (e) => {
        const { name, value } = e.target;

        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));

        if (name === "email") {
            setEmailValid(validateEmail(value));
        } else if (name === "password") {
            setPasswordValid(validatePassword(value));
        } else if (name === "username") {
            await validateUsername(value);
        }

    };
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('referral');
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const email = formData.email.trim().toLowerCase();
        const password = formData.password;
        const username = formData.username.trim();
        const role = formData.role;
        const contentFocus = formData.contentFocus;
        const mainGame = selectedCategory?.name?.trim();

        if (categoriesLoading) {
            setFormError("Categories are still loading. Please wait a moment.");
            return;
        }

        if (categoriesError || categories.length === 0) {
            setFormError("We could not load categories right now. Please try again before signing up.");
            return;
        }

        if (isUsernameChecking) {
            setFormError("We are still checking your username. Please wait a moment.");
            return;
        }

        if (usernameCheckError) {
            setFormError(usernameCheckError);
            return;
        }

        if (!username || !email || !password || !mainGame || !role || !contentFocus) {
            setFormError("Please complete every required field before signing up.");
            return;
        }

        if (signupIntent?.requiredRole && roleLabelForSignup(signupIntent.requiredRole) !== role) {
            setFormError(
                `Choose ${roleLabelForSignup(
                    signupIntent.requiredRole
                )} to continue with this upgrade.`
            );
            return;
        }

        if (!validateEmail(email)) {
            setEmailValid(false);
            setFormError("Please enter a valid email address.");
            return;
        }

        if (!validatePassword(password)) {
            setPasswordValid(false);
            setFormError("Password must be at least 8 characters.");
            return;
        }

        if (!/^[a-zA-Z0-9_]{3,20}$/.test(username)) {
            setUsernameInvalid(true);
            setFormError("Username must be 3-20 characters with only letters, numbers, or underscores.");
            return;
        }

        if (UnusableWords.some(word => username.toLowerCase().includes(word.toLowerCase()))) {
            setUsernameInappropriate(true);
            setFormError("Username contains inappropriate words.");
            return;
        }

        if (isUsernameInvalid || isUsernameInappropriate || isUsernameTaken) {
            setFormError("Please fix the username issue before signing up.");
            return;
        }

        if (!agreeToTerms) {
            setFormError('You must agree to the terms and conditions to sign up.');
            return;
        }

        setFormError(null);
        setIsSubmitting(true);
        let createdUser = null;
        let profileCreated = false;
        try {
            const matchingProfiles = await listPublicProfiles({ username });
            if (matchingProfiles.length > 0) {
                setUsernameTaken(true);
                setFormError("Username already taken.");
                setIsSubmitting(false);
                return;
            }

            isCreatingAccountRef.current = true;
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            createdUser = user;

            await setDoc(doc(db, 'Users', user.uid), {
                profileInfo: {
                    email: user.email,
                    username: username,
                    role: role,
                    phoneNumber: "",
                    mainGame,
                    contentFocus,
                    id: user.uid,
                    about: "",
                    points: 20,
                    profilePicUrl:
                        "https://static.vecteezy.com/system/resources/previews/036/280/650/original/default-avatar-profile-icon-social-media-user-image-gray-avatar-icon-blank-profile-silhouette-illustration-vector.jpg",
                },
                referrals: {
                    referredBy: referrer != null ? referrer : "anteos1",
                    hasRecievedReferralBonus: false
                },
                security: {
                    questions: {},
                },
                socials: {
                    tiktok: "",
                    twitter: "",
                    twitch: "",
                    instagram: "",
                    youtube: "",
                },
                orders: {
                    previous: {},
                    pending: {},
                    current: {},
                },
            }, { merge: true });
            profileCreated = true;
            trackEvent("sign_up", {
                method: "email",
                role,
                content_focus: contentFocus,
            });

            if (nextPath === "/stats" || nextPath.startsWith("/stats?")) trackStatsEvent("stats_signup_complete");

            if (joinEmailList) {
                try {
                    await axios.post(`${process.env.REACT_APP_ENDPOINT}/api/add-to-mailchimp`, {
                        email: user.email,
                        username: username || '',
                        role: role,
                    });

                    await setDoc(doc(db, "EmailList", user.uid), {
                        email: user.email,
                    });

                    await setDoc(doc(db, "registrations", user.uid), {
                        emailAddress: user.email,
                        activity: ["reminder_email"],
                    });
                } catch (err) {
                    console.error("Email list signup error:", err);
                }
            }



            if (signupIntent) {
                const path =
                    signupIntent.type === "subscription"
                        ? "/api/stripe/create-subscription-checkout"
                        : "/api/stripe/create-purchase-checkout";
                const payload =
                    signupIntent.type === "subscription"
                        ? {
                            uid: user.uid,
                            planId: signupIntent.id,
                            billingInterval: signupIntent.billingInterval,
                        }
                        : {
                            uid: user.uid,
                            productId: signupIntent.id,
                        };

                try {
                    const session = await createPricingCheckoutSession({
                        endpoint: process.env.REACT_APP_ENDPOINT,
                        path,
                        payload,
                    });
                    assignSafeExternalUrl(session.url);
                    return;
                } catch (checkoutError) {
                    logAuthError("Post-signup checkout failed", checkoutError, {
                        flow: "signup_checkout_intent",
                        intentType: signupIntent.type,
                        intentId: signupIntent.id,
                    });
                    navigate(
                        signupIntent.type === "subscription"
                            ? "/pricing?checkout=cancelled"
                            : "/pricing?purchase=cancelled"
                    );
                    return;
                }
            }

            navigate(withNextPath("/onboarding", nextPath));
        } catch (error) {
            logAuthError("Signup failed", error, {
                flow: "signup",
                emailDomain: email.split("@")[1] || "unknown",
                role,
                mainGame: mainGame || "missing",
                contentFocus: contentFocus || "missing",
                profileCreated,
                accountCreated: Boolean(createdUser),
            });

            if (createdUser && !profileCreated) {
                try {
                    await deleteUser(createdUser);
                } catch (deleteError) {
                    logAuthError("Signup cleanup failed", deleteError, {
                        flow: "signup",
                        userId: createdUser.uid,
                    });
                    await signOut(auth).catch((signOutError) => {
                        logAuthError("Signup sign out failed", signOutError, {
                            flow: "signup",
                            userId: createdUser.uid,
                        });
                    });
                }
            }

            setFormError(
                getAuthErrorMessage(
                    error,
                    createdUser && !profileCreated
                        ? "Your login was created, but we could not finish your profile. Please try again."
                        : "We could not create your account. Please try again."
                )
            );
            isCreatingAccountRef.current = false;
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <SEO
                title="Join Kre8 | Twitch Editors, Clippers, and Creators"
                description="Create a Kre8 account to hire Twitch editors, join as an editor or clipper, get early access to clipping campaigns, and start collaborating."
                canonicalPath="/signup"
                noindex
            />
            <div
            className="mobile-safe relative min-h-screen overflow-hidden bg-black text-white"
            style={{
                backgroundImage: `url(${Edit})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        >
            <div className="absolute inset-0 bg-black/75" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-black" />
            <div className="absolute -top-24 left-[-8rem] h-72 w-72 rounded-full bg-[#9146FF]/20 blur-3xl sm:h-[28rem] sm:w-[28rem]" />
            <div className="absolute bottom-[-8rem] right-[-7rem] h-72 w-72 rounded-full bg-[#84ff00]/10 blur-3xl sm:h-[24rem] sm:w-[24rem]" />
            <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 py-8 sm:py-10">
                <div className="mb-7 max-w-[36rem] text-center sm:mb-10">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#84ff00] sm:text-sm sm:tracking-[0.2em]">
                        Join Kre8
                    </p>

                    <h1 className="text-3xl font-black leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                        Create Videos at a
                        <span className="block bg-gradient-to-r from-[#84ff00] to-[#9146FF] bg-clip-text text-transparent">
                            Bigger Scale
                        </span>
                    </h1>

                    <p className="mx-auto mt-4 max-w-xl text-sm text-zinc-400 sm:mt-5 sm:text-lg">
                        Connect with Twitch and gaming editors and grow your content faster.
                    </p>

                </div>
                <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-black/45 p-5 shadow-2xl backdrop-blur-xl sm:p-8 lg:p-10">

                    <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-zinc-300">
                                Username
                            </label>

                            <input
                                id="username"
                                name="username"
                                type="text"
                                value={formData.username}
                                onChange={handleChange}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                autoComplete="username"
                                required
                                placeholder="yourname"
                                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#84ff00]"
                            />

                            {isUsernameInvalid && (
                                <p className="mt-2 text-sm text-red-400">
                                    3–20 characters with only letters, numbers, or underscores.
                                </p>
                            )}

                            {isUsernameInappropriate && (
                                <p className="mt-2 text-sm text-red-400">
                                    Username contains inappropriate words.
                                </p>
                            )}

                            {isUsernameTaken && (
                                <p className="mt-2 text-sm text-red-400">
                                    Username already taken.
                                </p>
                            )}

                            {isUsernameChecking && (
                                <p className="mt-2 text-sm text-zinc-400">
                                    Checking username availability...
                                </p>
                            )}

                            {usernameCheckError && (
                                <div className="mt-2 flex flex-col gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-3 text-sm text-red-300 sm:flex-row sm:items-center sm:justify-between">
                                    <span>{usernameCheckError}</span>
                                    <button
                                        type="button"
                                        onClick={() => validateUsername(formData.username.trim())}
                                        className="rounded-lg border border-red-300/30 px-3 py-1 text-xs font-semibold text-red-100 transition hover:bg-red-500/20"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-zinc-300">
                                Email
                            </label>

                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck="false"
                                autoComplete="email"
                                inputMode="email"
                                required
                                placeholder="you@example.com"
                                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#84ff00]"
                            />

                            {!isEmailValid && (
                                <p className="mt-2 text-sm text-red-400">
                                    Email is invalid.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-zinc-300">
                                Password
                            </label>

                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    value={formData.password}
                                    onChange={handleChange}
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    autoComplete="new-password"
                                    required
                                    placeholder="At least 8 characters"
                                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 pr-12 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#84ff00]"
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-5 w-5" />
                                    ) : (
                                        <Eye className="h-5 w-5" />
                                    )}
                                </button>
                            </div>

                            {!isPasswordValid && (
                                <p className="mt-2 text-sm text-red-400">
                                    Password must be at least 8 characters.
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-zinc-300">
                                Select Your Main Content Category
                            </label>

                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!categoriesLoading && !categoriesError) {
                                            setGameOpen(!gameOpen);
                                        }
                                    }}
                                    disabled={categoriesLoading || Boolean(categoriesError)}
                                    className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm text-white disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <span>
                                        {selectedCategory
                                            ? selectedCategory.name
                                            : categoriesLoading
                                                ? "Loading categories..."
                                                : categoriesError
                                                    ? "Categories unavailable"
                                                    : categories.length > 0
                                                ? "Select your main category"
                                                : "No categories available"}
                                    </span>

                                    <ChevronDown className="h-4 w-4 text-zinc-400" />
                                </button>

                                {gameOpen && (
                                    <div className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-2xl border border-white/10 bg-[#0d0d0d] shadow-2xl">
                                        {categories.map((category) => (
                                            <button
                                                type="button"
                                                key={category.id}
                                                onClick={() => {
                                                    setSelectedCategory(category);
                                                    setGameOpen(false);
                                                }}
                                                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm text-zinc-300 hover:bg-white/5"
                                            >
                                                {category.name}

                                                {selectedCategory?.id === category.id && (
                                                    <Check className="h-4 w-4 text-[#84ff00]" />
                                                )}
                                            </button>
                                        ))}
                                        {categories.length === 0 && (
                                            <div className="px-4 py-3 text-sm text-zinc-500">
                                                No categories available. Please refresh and try again.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {categoriesError && (
                                <div className="mt-2 flex flex-col gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-3 text-sm text-red-300 sm:flex-row sm:items-center sm:justify-between">
                                    <span>{categoriesError}</span>
                                    <button
                                        type="button"
                                        onClick={fetchCategories}
                                        className="rounded-lg border border-red-300/30 px-3 py-1 text-xs font-semibold text-red-100 transition hover:bg-red-500/20"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}
                        </div>

                        {signupIntent?.requiredRole && (
                            <div className="rounded-2xl border border-emerald-300/20 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-100">
                                Your {roleLabelForSignup(signupIntent.requiredRole)} role is selected for this upgrade.
                            </div>
                        )}

                        <div>
                            <label className="mb-3 block text-sm font-medium text-zinc-300">
                                Choose Your Role
                            </label>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">

                                <button
                                    type="button"
                                    onClick={() => handleRoleChange("Creator")}
                                    className={`rounded-2xl border p-4 transition ${
                                        formData.role === "Creator"
                                            ? "border-[#84ff00] bg-[#84ff00]/10"
                                            : "border-white/10 bg-white/5 hover:bg-white/10"
                                    }`}
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <Gamepad2 className="h-6 w-6 text-[#84ff00]" />
                                        <span className="font-semibold">
                                            Creator
                                        </span>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => handleRoleChange("Editor")}
                                    className={`rounded-2xl border p-4 transition ${
                                        formData.role === "Editor"
                                            ? "border-[#9146FF] bg-[#9146FF]/10"
                                            : "border-white/10 bg-white/5 hover:bg-white/10"
                                    }`}
                                >
                                    <div className="flex flex-col items-center gap-2">
                                        <Scissors className="h-6 w-6 text-[#9146FF]" />
                                        <span className="font-semibold">
                                            Editor or Clipper
                                        </span>
                                    </div>
                                </button>

                            </div>
                        </div>
                        {formData.role && (
                            <div>
                                <label className="mb-3 block text-sm font-medium text-zinc-300">
                                    {formData.role === "Creator"
                                        ? "What kind of content help do you want?"
                                        : "What do you mainly offer?"}
                                </label>

                                <div className="grid gap-3">
                                    {contentFocusOptions.map((option) => {
                                        const active = formData.contentFocus === option.value;
                                        return (
                                            <button
                                                type="button"
                                                key={option.value}
                                                onClick={() => handleContentFocusChange(option.value)}
                                                className={`rounded-2xl border p-4 text-left transition ${
                                                    active
                                                        ? "border-[#84ff00] bg-[#84ff00]/10"
                                                        : "border-white/10 bg-white/5 hover:bg-white/10"
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <p className="text-sm font-semibold text-white">
                                                            {option.label}
                                                        </p>
                                                        <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                                            {option.description}
                                                        </p>
                                                    </div>
                                                    {active && <Check className="h-4 w-4 shrink-0 text-[#84ff00]" />}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        <div className="space-y-4">

                            <label className="flex items-start gap-3 text-sm text-zinc-400">
                                <input
                                    type="checkbox"
                                    name="agreeToTerms"
                                    checked={agreeToTerms}
                                    onChange={handleCheckboxChange}
                                    className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-[#84ff00] focus:ring-[#84ff00]"
                                />

                                <span>
                                    I agree to the{" "}
                                    <a
                                        href="/legal/terms"
                                        className="text-[#84ff00] hover:text-white"
                                    >
                                        Terms
                                    </a>
                                    ,{" "}
                                    <a
                                        href="/legal/privacy"
                                        className="text-[#84ff00] hover:text-white"
                                    >
                                        Privacy
                                    </a>
                                    , and{" "}
                                    <a
                                        href="/legal/cookies"
                                        className="text-[#84ff00] hover:text-white"
                                    >
                                        Cookie Policy
                                    </a>
                                    .
                                </span>
                            </label>

                            <label className="flex items-start gap-3 text-sm text-zinc-400">
                                <input
                                    type="checkbox"
                                    name="joinEmailList"
                                    checked={joinEmailList}
                                    onChange={handleCheckboxChange}
                                    className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-[#84ff00] focus:ring-[#84ff00]"
                                />

                                <span>
                                    Join our email list for updates and special offers
                                </span>
                            </label>

                        </div>

                        {formError && (
                            <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                                {formError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={
                                isSubmitting ||
                                categoriesLoading ||
                                Boolean(categoriesError) ||
                                isUsernameChecking ||
                                Boolean(usernameCheckError)
                            }
                            className="w-full rounded-2xl bg-purple-400 px-6 py-4 text-sm font-bold text-white transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                            style={{backgroundColor:"#7F00FF"}}
                        >
                            {isSubmitting ? "Creating account..." : "Signup"}
                        </button>

                        <p className="text-center text-xs text-zinc-500">
                            Free to join · Start in under a minute
                        </p>
                        <p className="mt-4 text-sm text-zinc-500 text-center">
                        Already a member?{" "}
                        <Link
                            to={withNextPath("/login", nextPath)}
                            className="font-semibold text-[#84ff00]"
                        >
                            Log in here
                        </Link>
                    </p>
                    </form>
                </div>

                <p className="mt-8 text-center text-sm text-zinc-500">
                    By signing up, you join creators scaling their content faster.
                </p>
            </div>
        </div>
    </>

    );
}

export default Signup;
